import type { UseFormReturn, DefaultValues } from 'react-hook-form';
import type { ITicket, ITicketCreate } from 'src/pages/tickets/tickets.types';
import type { IContact, IContactCreate } from 'src/pages/contacts/contacts.types';
import type { IAccount, IAccountCreate } from 'src/pages/accounts/accounts.types';

import { useForm } from 'react-hook-form';
import { useMemo, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';

import validationSchema from 'src/utils/validationSchema';
import { dynamicShape } from 'src/utils/customFieldValidationSchema';

import { useTranslate } from 'src/locales';

type Module = 'company' | 'contact' | 'ticket';

type CreateByModule = {
  company: IAccountCreate;
  contact: IContactCreate;
  ticket: ITicketCreate;
};

type RecordByModule = {
  company: IAccount;
  contact: IContact;
  ticket: ITicket;
};

interface CustomFieldDef {
  name: string;
  type: string; // narrow if you have a union
}

interface UseHandleFormProps<M extends Module> {
  isDialogOpen: boolean;
  module: M;
  fldList: { name: string; required: boolean }[];
  customFields?: CustomFieldDef[];
  selectedRecord?: RecordByModule[M] | null;
}

type UseHandleFormReturn<M extends Module> = UseFormReturn<CreateByModule[M]> & { formKey: string };

// keep your helper (note: shallow + string-only)
function convertNullToString<T extends Record<string, unknown>>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, value === null ? '' : value])
  ) as T;
}

function getFormDefaultValue<M extends Module>(module: M): CreateByModule[M] {
  switch (module) {
    case 'company':
      return {
        name: '',
        email: '',
        phone: '',
        crNumber: '',
        pincode: '',
        city: '',
        country: '',
        website: '',
        address: '',
        assignUsers: [],
      } as CreateByModule[M];
    case 'contact':
      return {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        nationalId: '',
        country: '',
        companyId: '',
        lifecycleStage: '',
        assignUsers: [],
      } as CreateByModule[M];
    case 'ticket':
      return {
        title: '',
        contactId: '',
        assignUsers: [],
        status: 'open',
        priority: 'normal',
        description: '',
      } as CreateByModule[M];
    default:
      throw new Error(`Unhandled module: ${String(module)}`);
  }
}

// 1) Strongly typed builders per module
const BUILDERS = {
  company: (r: RecordByModule['company'] | null | undefined): Partial<CreateByModule['company']> =>
    r
      ? convertNullToString({
          name: r.name ?? '',
          email: r.email ?? '',
          phone: r.phone ?? '',
          crNumber: r.crNumber ?? '',
          pincode: r.pincode ?? '',
          city: r.city ?? '',
          country: r.country ?? '',
          website: r.website ?? '',
          address: r.address ?? '',
          assignUsers: r.CompanyAssign ?? [],
          ...(r.customData ?? {}),
        })
      : {},

  contact: (r: RecordByModule['contact'] | null | undefined): Partial<CreateByModule['contact']> =>
    r
      ? convertNullToString({
          firstName: r.firstName ?? '',
          lastName: r.lastName ?? '',
          email: r.email ?? '',
          phone: r.phone ?? '',
          address: r.address ?? '',
          city: r.city ?? '',
          nationalId: r.nationalId ?? '',
          country: r.country ?? '',
          companyId: r.companyId ?? '',
          assignUsers: r.ContactAssign ?? [],
          lifecycleStage: r.lifecycleStageId ?? r.lifecycleStage?.id ?? '',
          ...(r.customData ?? {}),
        })
      : {},

  ticket: (r: RecordByModule['ticket'] | null | undefined): Partial<CreateByModule['ticket']> =>
    convertNullToString({
      title: r?.title ?? '',
      status: (r?.status ?? 'open')?.toLowerCase(),
      priority: r?.priority ?? 'normal',
      description: r?.description ?? '',
      assignUsers: r?.TicketAssign ?? [],
      contactId: r?.contactId ?? '',
      ...(r?.customData ?? {}),
    }),
} as const;

// 2) Generic, tiny dispatcher — no overloads, no ugly casts
function getModuleFormValue<M extends Module>(
  module: M,
  selectedRcd: RecordByModule[M] | null | undefined
): Partial<CreateByModule[M]> {
  return BUILDERS[module](selectedRcd as any) as Partial<CreateByModule[M]>;
}

export function useHandleForm<M extends Module>({
  isDialogOpen,
  module,
  fldList,
  customFields,
  selectedRecord = null,
}: UseHandleFormProps<M>): UseHandleFormReturn<M> {
  const { t } = useTranslate('translation');

  const FormSchema = useMemo(() => {
    const base = validationSchema(fldList, t);
    const dynamic = dynamicShape(customFields, t);
    return base.merge(dynamic).passthrough();
  }, [fldList, customFields, t]);

  // 1) Compute dynamic defaults from custom fields
  const customFieldDefaults = useMemo(() => {
    const byType: Record<string, unknown> = {
      text: '',
      number: 0,
      boolean: false,
      select: '',
      multiSelect: [],
      date: '',
    };
    return (customFields ?? []).reduce(
      (acc, f) => {
        acc[f.name] = byType[f.type] ?? '';
        return acc;
      },
      {} as Record<string, unknown>
    );
  }, [customFields]);

  // Keep a correlated alias so TS doesn't widen to the union
  type FormValues = CreateByModule[M];

  // 2) Build initial form values (create vs edit)
  const initialValues = useMemo<FormValues>(() => {
    const defaults = getFormDefaultValue(module);
    const fromRecord = getModuleFormValue(module, selectedRecord);
    return { ...defaults, ...customFieldDefaults, ...fromRecord } as FormValues;
  }, [module, selectedRecord, customFieldDefaults]);

  // 3) Create a stable key so the form remounts if customFields shape changes
  const formKey = useMemo(() => {
    const id =
      selectedRecord && typeof selectedRecord === 'object' && 'id' in selectedRecord
        ? String((selectedRecord as { id?: string | number }).id ?? 'new')
        : 'new';

    const shape = (customFields ?? []).map((f) => `${f.name}:${f.type}`).join('|');

    return `${id}::${shape}`;
  }, [selectedRecord, customFields]);

  const methods = useForm<FormValues>({
    mode: 'all',
    resolver: zodResolver(FormSchema),
    // Cast to RHF's DefaultValues to satisfy the generic while keeping correlation
    defaultValues: initialValues as unknown as DefaultValues<FormValues>,
    shouldUnregister: true,
  });

  useEffect(() => {
    if (!isDialogOpen) return;
    methods.reset(initialValues as DefaultValues<FormValues>);
  }, [isDialogOpen, initialValues, methods]);

  return { ...methods, formKey } as UseHandleFormReturn<M>;
}
